import { connect } from 'react-redux';
import { useState, useEffect } from 'react';
import { NumericFormat } from 'react-number-format';
import { format, addYears, addDays } from 'date-fns';
import { parseFromTimeZone } from 'date-fns-timezone';

import { parseDateToTz } from '../utils/GlobalFunctions';
import { Actions } from '../../store/actions/organisationActions';

import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { TableRow, TableCell } from '../ui/table';
import { Tooltip, TooltipTrigger, TooltipContent } from '../ui/tooltip';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { CircleHelp, Save, X, Info, Pencil, Trash2 } from 'lucide-react';

const OrganisationMetricRow = ({
  metric,
  reportingPeriods,
  orgMetrics,
  saveOrganisationMetric,
  list,
  toggleModal,
  cyfAdmin,
  branding,
  currency,
}) => {
  const { primaryColor, tooltipBackground, tooltipText } = branding?.colors || {};

  const [editing, setEditing] = useState(metric.id === metric.uuid);
  const [editedState, setEditedState] = useState(null);
  const [invalidInput, setInvalidInput] = useState(false);

  useEffect(() => {
    if (editing) {
      setEditedState({ ...metric });
    }
  }, [editing, metric]);

  // Filter reporting periods to exclude those used by other metrics, except the current metric
  const filteredReportingPeriods = reportingPeriods.filter(
    (report) => !list.some((m) => m.id !== metric.id && m.startDate === report.startDate),
  );

  const updateField = (name, value) => {
    const newEditedState = { ...editedState, [name]: value };
    const hasZero = Object.values(newEditedState).some((val) => val === '0');
    setInvalidInput(hasZero);
    setEditedState(newEditedState);
  };

  if (editing) {
    const selectedPeriodValue = editedState?.startDate || metric.startDate;

    return (
      <TableRow>
        <TableCell>
          <Select
            value={selectedPeriodValue}
            onValueChange={(selectedStartDate) => {
              const selectedPeriod = reportingPeriods.find((period) => period.startDate === selectedStartDate);
              if (selectedPeriod) {
                setEditedState({
                  ...editedState,
                  year: String(selectedPeriod.value),
                  startDate: selectedPeriod.startDate,
                  endDate: selectedPeriod.endDate,
                });
              }
            }}>
            <SelectTrigger>
              <SelectValue placeholder='Select reporting period' />
            </SelectTrigger>
            <SelectContent>
              {filteredReportingPeriods.map((period) => (
                <SelectItem key={period.startDate} value={period.startDate}>
                  {period.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </TableCell>
        <TableCell>
          <NumericFormat
            thousandSeparator={true}
            customInput={Input}
            value={editedState?.originalTurnover || ''}
            onValueChange={(values) => updateField('originalTurnover', values.value)}
          />
        </TableCell>
        {orgMetrics.metric1 && orgMetrics.metric1.name !== 'revenue' && (
          <TableCell>
            <NumericFormat
              thousandSeparator={true}
              customInput={Input}
              value={editedState?.metric1 || ''}
              onValueChange={(values) => updateField('metric1', values.value)}
            />
          </TableCell>
        )}
        {orgMetrics.metric2 && orgMetrics.metric2.name !== 'revenue' && (
          <TableCell>
            <NumericFormat
              thousandSeparator={true}
              customInput={Input}
              value={editedState?.metric2 || ''}
              onValueChange={(values) => updateField('metric2', values.value)}
            />
          </TableCell>
        )}
        {orgMetrics.metric3 && orgMetrics.metric3.name !== 'revenue' && (
          <TableCell>
            <NumericFormat
              thousandSeparator={true}
              customInput={Input}
              value={editedState?.metric3 || ''}
              onValueChange={(values) => updateField('metric3', values.value)}
            />
          </TableCell>
        )}
        <TableCell className='text-right'>
          <div className='flex justify-end gap-2'>
            {invalidInput ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <CircleHelp size={18} strokeWidth={1.5} className='cursor-pointer' style={{ color: primaryColor }} />
                </TooltipTrigger>
                <TooltipContent>Cannot save 0 input values.</TooltipContent>
              </Tooltip>
            ) : (
              <Button
                variant='ghost'
                size='icon'
                onClick={() => {
                  saveOrganisationMetric({ ...metric, ...editedState, status: 'Active' });
                  setEditedState(null);
                  setEditing(false);
                }}>
                <Save size={18} strokeWidth={1.5} />
              </Button>
            )}
            {metric.id !== metric.uuid && (
              <Button
                variant='ghost'
                size='icon'
                onClick={() => {
                  setEditedState(null);
                  setEditing(false);
                }}>
                <X size={18} strokeWidth={1.5} />
              </Button>
            )}
          </div>
        </TableCell>
      </TableRow>
    );
  }

  const formatNumber = (num) => (num ? num.replace(/(?<!\.\d*)(?<=\d)(?=(?:\d{3})+(?!\d))/g, ',') : 'N/A');

  return (
    <TableRow>
      <TableCell>
        {metric.startDate && format(parseDateToTz(metric.startDate, 'yyyy-MM-dd'), 'do MMM yyyy')} -{' '}
        {metric.endDate && format(parseDateToTz(metric.endDate, 'yyyy-MM-dd'), 'do MMM yyyy')}
      </TableCell>
      <TableCell>
        {cyfAdmin ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                {formatNumber(metric.originalTurnover)}
                <Info size={16} strokeWidth={1.5} className='ml-1 cursor-pointer' style={{ color: primaryColor }} />
              </span>
            </TooltipTrigger>
            <TooltipContent>
              The Fx Rate used: 1 {currency?.isoCode} = {metric?.rate !== undefined ? metric.rate : 'N/A'} USD on{' '}
              {metric?.rateDate !== undefined ? metric.rateDate : 'N/A'}
            </TooltipContent>
          </Tooltip>
        ) : (
          formatNumber(metric.originalTurnover)
        )}
      </TableCell>
      {orgMetrics.metric1 && orgMetrics.metric1.name !== 'revenue' && <TableCell>{formatNumber(metric.metric1)}</TableCell>}
      {orgMetrics.metric2 && orgMetrics.metric2.name !== 'revenue' && <TableCell>{formatNumber(metric.metric2)}</TableCell>}
      {orgMetrics.metric3 && orgMetrics.metric3.name !== 'revenue' && <TableCell>{formatNumber(metric.metric3)}</TableCell>}
      <TableCell className='text-right'>
        <div className='flex justify-end gap-2'>
          <Button variant='ghost' size='icon' onClick={() => setEditing(true)}>
            <Pencil size={18} strokeWidth={1.5} />
          </Button>
          <Button variant='ghost' size='icon' onClick={() => toggleModal(true, metric, 'deleteOrgMetric')}>
            <Trash2 size={18} strokeWidth={1.5} />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
};

const mapStateToProps = (state) => {
  const organisation =
    state.organisation.details || state.organisation.list.find((item) => item.id === state.currentOrganisation);
  const thisYear = format(new Date(), 'yyyy');

  const lastYear =
    format(new Date(), 'MM-dd') >= `${organisation.reportingYearEndMonth}-${organisation.reportingYearEndDay}`
      ? +thisYear + +'1'
      : thisYear;

  const reportingYearEndMonth = organisation?.reportingMonth || organisation?.reportingYearEndMonth;
  const reportingYearEndDay = organisation?.reportingDay || organisation?.reportingYearEndDay;

  const reportingPeriods = [];

  if (organisation && reportingYearEndMonth && reportingYearEndDay) {
    for (var i = lastYear; i >= 2018; i--) {
      const endDate = parseFromTimeZone(new Date(`${i}-${reportingYearEndMonth}-${reportingYearEndDay}`), {
        timeZone: 'Europe/London',
      });
      const startDate = addDays(addYears(endDate, -1), 1);

      reportingPeriods.push({
        year: i,
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate: format(endDate, 'yyyy-MM-dd'),
        label: `${format(startDate, 'do MMM yyyy')} - ${format(endDate, 'do MMM yyyy')}`,
        value: i,
      });
    }
  }

  const orgCurrency = state?.organisation?.details?.currency;
  const currencies = state?.admin?.adminPortal?.currencies;
  const currency = currencies?.find((item) => item.id === orgCurrency);

  return { reportingPeriods, cyfAdmin: state.profile?.details?.cyfAdmin || false, branding: state.branding, currency };
};

const mapDispatchToProps = (dispatch) => ({
  saveOrganisationMetric: (organisationMetric) => dispatch(Actions.saveOrganisationMetric(organisationMetric)),
  toggleModal: (status, item, action) => dispatch(Actions.toggleModal(status, item, action)),
});

export default connect(mapStateToProps, mapDispatchToProps)(OrganisationMetricRow);
